import { Grid } from './Grid';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Paper, Alert, Chip, CircularProgress, Typography, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Tooltip, TextField } from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';
import notesService from '../../services/notesService';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/useAuth';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { getRxStatusLabel } from '../../constants/rxStatus';
import { RxAdjStateBadge, RxStatusBadge } from './RxStatusBadge';
import { describePmsGate } from '../order-fulfillment/pmsGateDisplay';
import type { PmsGateResult } from '../../types/pmsGate';
import type { PlanClaimStatus } from '../../types/billing.types';
import { TamperResistantText } from './TamperResistantText';
import { isPrescriberNoteEnabled } from '../../config/pharmacyFeatureFlags';
import type { VerificationPhoto } from '../../services/verificationPhotoService';
import { describeCapture } from '../product-verification/VerificationPhotoThumbnails';

/**
 * GP-1163 — Hard Copy typography.
 *
 * The hardcopy was rendered in Courier New at 13px (10px in compact panes).
 * Staff reading DINs, quantities, dates and drug names off it all shift called
 * it "blocky" and too small: Courier New is a narrow monospace with a small
 * x-height, it blurs the character pairs that matter most on a prescription
 * (I/l/1, O/0, 5/S, 8/B), and 13px = ~9.75pt sat BELOW the 10-11pt floor
 * healthcare/EMR guidance sets for clinical body text.
 *
 * Verdana replaces it: screen-designed, wide letter spacing, strong
 * differentiation of those same character pairs, uniform-width digits (so
 * price/quantity columns still line up without a monospace), and forgiving on
 * the lower-resolution monitors and remote-desktop sessions the pharmacies run.
 *
 * Sizes below are the agreed clinical scale, in pt converted to px at the CSS
 * 96dpi ratio (1pt = 4/3px) so the on-screen pane and the printed sheet agree:
 *   Drug name 13pt bold · Strength / SIG / LU / adjudication 12pt ·
 *   DIN / quantity / dates / pricing 11pt · notes & tabular history 10.5pt.
 * Nothing gets smaller than it is today.
 *
 * The `compact` variants matter most: Clinical Verification and Product
 * Verification both render this viewer with compact={true}, so that is the pane
 * a pharmacist reads all shift, and it is where the complaint originated (its
 * fields were 11px Courier). Compact body lands at 13px rather than the full
 * 15px because the CV pane is a narrow flex column — but the gain is larger than
 * the px delta suggests: Verdana's x-height is ~0.55em against Courier New's
 * ~0.42em, so 13px Verdana renders letterforms about the size of 17px Courier.
 * That x-height difference is also why the pt targets below are met at these px
 * values rather than needing a further bump.
 */
export const HARDCOPY_FONT_STACK =
  "Verdana, 'Verdana Ref', 'DejaVu Sans', Geneva, Tahoma, sans-serif";

/** Line spacing, +15% over the previous 1.3 (spacing beats size for scan accuracy). */
export const HARDCOPY_LINE_HEIGHT = 1.5;

export const HARDCOPY_FONT_SIZES = {
  /** Rx number + patient name — the identity line (15pt / 12pt, bold). */
  identity: { full: '20px', compact: '16px' },
  /** Drug name — the single biggest scan target (13pt / 11pt, bold). */
  primary: { full: '17px', compact: '15px' },
  /** Strength, SIG, LU code, adjudication, prescriber name (12pt / 10.5pt). */
  emphasis: { full: '16px', compact: '14px' },
  /** DIN, quantity, dates, pricing, payer messages (11pt / 10pt). */
  body: { full: '15px', compact: '13px' },
  /** Notes, counsel checkboxes, trace/ref, fill-history table (10.5pt / 9pt). */
  detail: { full: '14px', compact: '12px' },
  /** Signature-box chrome only — label + signed-at stamp (9pt / 8pt). */
  micro: { full: '12px', compact: '11px' },
} as const;

/** Resolve a scale role for the current density. */
export const hardcopyFontSize = (
  role: keyof typeof HARDCOPY_FONT_SIZES,
  compact: boolean,
): string => (compact ? HARDCOPY_FONT_SIZES[role].compact : HARDCOPY_FONT_SIZES[role].full);

/**
 * GP-1293: dosage-form abbreviation for the quantity + drug-name line.
 *
 * Kroll's DrgFormMast.Form arrives as "<ABBREV> (<description>)" — e.g.
 * "PEN (Injection)", "TAB (Tablet)", "SYR (Syringe)" — and the hardcopy already
 * prints that whole string on its own line. The pharmacy wants only the
 * abbreviation up on the drug line ("3 PEN Ajovy Autoinjector 225mg/1.5ml"), so
 * take the leading token before the first space/bracket. Kroll's own casing is
 * preserved — we never invent or remap an abbreviation.
 *
 * Returns '' when Kroll has no form (compounds/mixtures), leaving the drug line
 * exactly as it was.
 */
export const getDrugFormAbbreviation = (drugForm: string | null | undefined): string => {
  if (!drugForm) return '';
  return drugForm.trim().split(/[\s(]+/)[0] || '';
};

/**
 * Extract initials from a full name
 * @param fullName - Full name string (e.g., "John Doe")
 * @returns Initials (e.g., "JD")
 */
export const getUserInitials = (fullName: string | null | undefined): string => {
  if (!fullName) return '';
  return fullName
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || '')
    .join('');
};

/**
 * Hardcopy data structure returned from the backend
 */
/**
 * Signature type for hardcopy verification
 */
export type SignatureType =
  | 'data_verification'
  | 'clinical_verification'
  | 'product_verification'
  | 'dispensing';

/**
 * The three signatures the Verification Signatures panel renders a box for.
 * `dispensing` is deliberately excluded — it is not one of the panel's boxes, so
 * it must not satisfy the "some verification was captured" check (GP-1406).
 */
export const VERIFICATION_SIGNATURE_TYPES: readonly SignatureType[] = [
  'data_verification',
  'clinical_verification',
  'product_verification',
];

/**
 * Signature data structure
 */
export interface HardcopySignature {
  id?: string;
  pharmacyId?: string;
  rxNumber: string;
  signatureType: SignatureType;
  initials: string;
  userEmail?: string;
  userName?: string;
  signedAt?: Date | string;
}

/** PRX-25: a Fillware per-Rx note (RxNotes). Kroll payloads carry no rxNotes. */
export interface HardcopyRxNote {
  id: number;
  rxNumber: string;
  noteType: number;
  noteTypeLabel: string;
  content: string;
  changed: string | null;
  createdBy: string;
}

export interface HardcopyData {
  /** PRX-25: Fillware per-prescription notes; absent on Kroll payloads. */
  rxNotes?: HardcopyRxNote[];
  // Rx Info
  rxNumber: number;
  origRxNum: number;
  fillDate: string;
  cancelDate: string | null;
  dispQty: number;
  /** Kroll Rx.PackSize - the per-pack size billed on this fill. The DIN line
      prints this (not DispQty): 180 dispensed / 60 per pack = 3 inhalers.
      Absent on payloads that predate the field. */
  packSize?: number | null;
  authQty: number;
  remQty: number;
  /** T038: Flag for unlimited refills (true when Kroll marks prescription as unlimited) */
  unlimitedRefills?: boolean;
  daysSupply: number;
  rxInit: string | null;
  userInit: string | null;
  sig: string;
  /** Kroll: the numeric Rx.Status code. Fillware: whatever the raw column
   *  held (often null) — PRX-71 stopped reading this for the Fillware label;
   *  `typeof status === 'number'` is how the viewer tells the two PMS apart. */
  status: number | null;
  /** PRX-71/PRX-59: `pmsRxGate(dbType, row)`'s verdict for this fill, for
   *  both PMS. `null` means the gate was never computed (never "Unknown"). */
  pmsGate?: PmsGateResult | null;
  /** Kroll Rx.AdjState (RxAdjState enum) - non-clean values (0,1,5,6,7) mean
      Kroll shows this fill as "Pending Adj"/"Needs Review" etc. while the
      numeric status still reads Active. Absent on payloads that predate it. */
  rxAdjState?: number | null;
  cost: number;
  markup: number;
  fee: number;
  mixFee: number;
  sscFee: number;
  retailPrice: number;

  // Patient Info
  patientLastName: string;
  patientFirstName: string;
  patientName: string;
  patientAddress: string;
  patientAddress2: string | null;
  patientCity: string;
  patientProvince: string;
  patientPostalCode: string;
  patientDOB: string;
  patientSex: string;
  patientPhone: string | null;

  // Drug Info
  brandName: string;
  genericName: string;
  drugStrength: string;
  drugDIN: string;
  drugForm: string | null;
  drugManufacturer: string | null;
  /** GP-1293: Kroll Drg.Description — the drug's physical-appearance description
   *  (e.g. "red cap, white body, imprint WY 5"). Same Kroll column the Packaging
   *  step's "Description" field reads; printed beside the DIN. Empty when Kroll
   *  has none (compounds/mixtures). */
  drugDescription?: string | null;
  /** GP-389: Flag indicating this is a compound/mixture prescription (Rx.MixId is not null) */
  isMixture?: boolean | number;

  // Doctor Info
  doctorLastName: string;
  doctorFirstName: string;
  doctorName: string;
  doctorAddress: string | null;
  doctorCity: string | null;
  doctorProvince: string | null;
  doctorLicence: string | null;
  doctorPhone: string | null;
  /** GP-647/GP-814: prescriber's General comment from Kroll DocCom, printed verbatim
   *  (holds the real out-of-province license, e.g. "AB #044452"). Empty when none. */
  doctorComment?: string | null;

  // Pricing
  insurancePays: number;
  customerPrice: number;
  /** null when the backend cannot compute it (Fillware fill with no drug cost): shown as unavailable. */
  grossProfitPercent: number | null;
  netPrice: number; // Used as "Old Price"
  totalDiscount: number;
  thirdPartyBreakdown: string | null;

  // History Info
  prevRxNum: string | null;
  prevFillDate: string | null;
  agoDays: number | null;
  firstRxNum: string | null;
  firstFillDate: string | null;

  // Extra Fields
  /** GP-814: Rx.WrittenDate — date the prescription was written by the prescriber */
  writtenDate?: string | null;
  /** GP-814: Rx.ProductSelection — product selection code (integer, shown as-is) */
  productSelection?: number | null;
  dueDate: string | null;
  refillStatus: string | null;
  copiedRxNum: string | null;
  /** Last actually-filled fill in the lineage (the "Last fill" line / Due basis) */
  lastFillRxNum: string | number | null;
  lastFillDate: string | null;
  /** GP-1039: product dispensed on that last actual fill — Kroll's "Old Drug"
   *  line. Empty string when the lineage has no prior fill. */
  prevDrugName?: string | null;
  prevDrugStrength?: string | null;
  prevDrugDIN?: string | null;
  /** Kroll Rx.Inactive — true when this fill has been superseded/inactivated (Copied to a newer Rx) */
  inactive?: boolean | number;
  /** Kroll Rx.OralWritten (varchar(1)): how the Rx was received — 'W'=Written, 'O'=Oral */
  oralWritten: string | null;
  isCharged: string | null;
  lot: string | null;
  drugExpiryDate: string | null;

  // Central Fill
  isCF: boolean;
  // Packaging
  packagingData?: PackagingItem[] | PackagingItem | null;
  // History
  history?: HardcopyHistoryItem[];
  // T197: Transfer In indicator (1 = transferred from another pharmacy, 0 = new at this pharmacy)
  isTransferIn?: number | boolean;
  // LU Code (ODB Limited Use)
  luCode?: string;
  luExpiryDate?: string | null;
  /** GP-814: ClientID of the patient's "PI" plan (BC PharmaCare / Pharmanet).
   *  For BC patients this is also the provincial Health Card Number (PHN). */
  piClientId?: string | null;
  /** GP-1006: per-plan adjudication details (submitted intervention codes +
   *  third-party response) from the latest Kroll RxPlnAdj transmission. */
  adjudications?: HardcopyAdjudication[] | null;
  /** PRX-165 WP-23, Fillware only: the claims could not be read. */
  adjudicationsUnavailable?: boolean;
}

/** GP-1006: one third-party plan's latest adjudication (Kroll RxPln + RxPlnAdj).
 *  Codes are shown verbatim — Kroll has no description lookup for intervention
 *  or CPhA response codes. */
export interface HardcopyAdjudication {
  planCode: string;
  seq: number;
  /** RxPln.TranType: 1 = claim, 3 = rejection */
  tranType: number;
  /** RxPln.Interventions — intervention codes on the plan row */
  interventionsSubmitted: string;
  /** RxPln.ReasonCode — LU code submitted with the claim */
  luCode: string;
  /** RxPlnAdj.ResultCode — payer result, e.g. 'A' = accepted */
  resultCode: string | null;
  /** RxPlnAdj.ErrorCodes — concatenated 2-char CPhA response codes, e.g. 'CK' */
  responseCodes: string;
  /** RxPlnAdj.InterventionCodes — intervention codes as transmitted */
  interventionCodes: string;
  traceNum: string | null;
  refNum: string | null;
  adjResponseDate: string | null;
  /** null only on Fillware: the paid amount is unavailable (PRX-165 WP-23). */
  planPays: number | string | null;
  /** RxPlnAdj.Messages — payer plain-text messages (CR-separated lines) */
  messages: string;
  /** PRX-165 WP-23, Fillware only: Fillware.dbo.Plans.Name for the plan code. */
  planName?: string | null;
  /** PRX-165 WP-23, Fillware only: the BIN the claim went to — the only name
   *  a rejected plan taken off the fill still has. */
  bin?: string | null;
  /** PRX-165 WP-23b, Fillware only: the claim's status. A reversed claim is
   *  labelled "Reversed" and shows no paid amount. Kroll rows never carry it. */
  claimStatus?: PlanClaimStatus;
}

export interface HardcopyHistoryItem {
  rxNumber: number;
  fillDate: string;
  /** See `HardcopyData.status` — Kroll numeric, Fillware often null. */
  status: number | null;
  /** PRX-71: same gate as `HardcopyData.pmsGate`, computed per history row. */
  pmsGate?: PmsGateResult | null;
  dispQty: number;
  daysSupply: number;
  daysInterval: number | null;
  retailPrice: number;
  customerPrice: number;
  insurancePays: number;
  drugName?: string;
  din?: string;
}

interface PackagingScan {
  lotNumber?: string;
  expiryDate?: string;
  timestamp?: string;
  confirmed?: boolean;
  barcode?: string;
  quantity?: number; // GP-384: Quantity from this stock bottle (part-packaging)
}

interface PackagingItem {
  rxNumber?: string;
  din?: string;
  lotNumber?: string;
  expiryDate?: string;
  scans?: PackagingScan[];
  quantityPackaged?: number | string;
  packageType?: string;
}

type PackagingDataInput = PackagingItem[] | PackagingItem | null | undefined;

type PackagingSummary = {
  lotNumber?: string;
  expiryDate?: string;
  quantity?: number | null;
  packageType?: string;
};

/**
 * T036: Get fill type label based on fill history
 * Returns 'First Fill' when:
 * - No previous fill exists, OR
 * - Rx number matches original Rx number, OR
 * - Auth = Rem + dispQty (meaning no fills have been consumed yet)
 * Otherwise returns 'Refill'
 */
export const getFillTypeLabel = (
  prevFillDate: string | null | undefined,
  rxNumber?: number,
  origRxNum?: number,
  authQty?: number | null,
  remQty?: number | null,
  dispQty?: number | null,
): string => {
  // If no previous fill date, this is a first fill
  if (!prevFillDate) {
    return 'First Fill';
  }
  // If this Rx number matches the original Rx number, it's the first fill
  if (rxNumber && origRxNum && rxNumber === origRxNum) {
    return 'First Fill';
  }
  // If Auth = Rem + dispQty, this is the first fill (no refills consumed yet)
  // Handle unlimited refills (-1) by skipping this check
  if (
    authQty !== null &&
    authQty !== undefined &&
    authQty !== -1 &&
    remQty !== null &&
    remQty !== undefined &&
    remQty !== -1 &&
    dispQty !== null &&
    dispQty !== undefined &&
    authQty === remQty + dispQty
  ) {
    return 'First Fill';
  }
  return 'Refill';
};

/**
 * T037: Get refills remaining display string
 * Handles unlimited refills (-1 value or unlimitedRefills flag)
 */
export const getRefillsDisplay = (
  remQty: number | null | undefined,
  authQty: number | null | undefined,
  unlimitedRefills?: boolean,
): { remaining: string; authorized: string } => {
  // Handle unlimited refills
  if (unlimitedRefills || remQty === -1 || authQty === -1) {
    return {
      remaining: remQty === -1 || unlimitedRefills ? '∞' : String(remQty ?? 0),
      authorized: authQty === -1 || unlimitedRefills ? '∞' : String(authQty ?? 0),
    };
  }

  return {
    remaining: String(remQty ?? 0),
    authorized: String(authQty ?? 0),
  };
};

/**
 * T195/T196: Get transfer status label based on Kroll transfer data
 * Returns 'Transferred In' if prescription was transferred from another pharmacy,
 * 'New Start' if this is a new prescription at this pharmacy.
 *
 * This helps pharmacists distinguish between:
 * - Patients who were already taking a medication (transferred from another pharmacy)
 * - Patients starting a medication for the first time (new start)
 */
export const getTransferStatusLabel = (
  isTransferIn: number | boolean | null | undefined,
): string => {
  // Convert to boolean for flexibility (handles numeric 1/0 and boolean values)
  const isTransfer = isTransferIn === 1 || isTransferIn === true;
  return isTransfer ? 'Transferred In' : 'New Start';
};

/**
 * Map Kroll's Rx.OralWritten code (varchar(1)) to the hardcopy "O/W" label.
 * Kroll records how the prescription was received: 'W' = Written, 'O' = Oral.
 * Unknown/other codes are returned verbatim so we never hide real Kroll data.
 */
export const getOralWrittenLabel = (
  oralWritten: string | null | undefined,
): string => {
  if (!oralWritten) return '';
  const code = oralWritten.trim().toUpperCase();
  switch (code) {
    case 'W':
      return 'Written';
    case 'O':
      return 'Oral';
    default:
      return oralWritten.trim();
  }
};

/**
 * GP-1039: Detect that the drug file changed between the last actual fill and
 * this one.
 *
 * When a drug is switched on an ACTIVE Rx without a new script (Ozempic
 * syringes → pens, Humira → citrate-free) Kroll prints an "Old Drug: ..." line
 * on its hardcopy. PHOX printed nothing, so an accidental switch could be
 * clinically verified and shipped without anyone noticing the product moved.
 *
 * The DIN is the product's identity, so it decides: a change is reported only
 * when both DINs are known and differ. Leading zeros are ignored — Kroll can
 * hold the same DIN padded differently across two drug files, and a padding
 * difference is not a drug change. Compounds/mixtures carry no DIN at all
 * (Kroll DrgMix), so for those the product description decides instead;
 * otherwise a compound switch could never be flagged.
 *
 * Returns null when there is no previous fill to compare — the caller renders
 * nothing rather than assuming anything about the prior product.
 */
export const getDrugChange = (
  data: Pick<
    HardcopyData,
    | 'drugDIN'
    | 'brandName'
    | 'genericName'
    | 'drugStrength'
    | 'prevDrugName'
    | 'prevDrugStrength'
    | 'prevDrugDIN'
  >,
): {
  previous: string;
  current: string;
  previousDIN: string;
  currentDIN: string;
} | null => {
  const previousName = (data.prevDrugName || '').trim();
  // No prior fill in this lineage (first fill) — nothing to compare against.
  if (!previousName) return null;

  const describe = (name: string, strength: string | null | undefined) =>
    [name, (strength || '').trim()].filter(Boolean).join(' ');

  const previous = describe(previousName, data.prevDrugStrength);
  const current = describe(
    (data.brandName || data.genericName || '').trim(),
    data.drugStrength,
  );

  const previousDIN = (data.prevDrugDIN || '').trim();
  const currentDIN = (data.drugDIN || '').trim();
  const normalizeDIN = (din: string) => din.replace(/^0+/, '');

  const changed =
    previousDIN && currentDIN
      ? normalizeDIN(previousDIN) !== normalizeDIN(currentDIN)
      : previous.toUpperCase() !== current.toUpperCase();

  if (!changed) return null;
  return { previous, current, previousDIN, currentDIN };
};

export interface HardcopyViewerProps {
  /** Prescription number to display */
  rxNumber: string;
  /** Optional pharmacy ID - falls back to activePharmacy */
  pharmacyId?: string;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Callback when data is loaded successfully */
  onLoaded?: (data: HardcopyData) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Pre-loaded hardcopy data (skip API call if provided) */
  initialData?: HardcopyData;
  /** Optional packaging data to surface lot/expiry on the hardcopy */
  packagingData?: PackagingDataInput;
  /** Callback when a history item is clicked */
  onRxClick?: (rxNumber: number) => void;
  /** Pre-loaded signatures for display */
  signatures?: HardcopySignature[];
  /** Current user's initials (for signing) */
  currentUserInitials?: string;
  /** Current user's email (for signing) */
  currentUserEmail?: string;
  /** Current user's full name (for signing) */
  currentUserName?: string;
  /** Callback when user signs a box (passes signature type) */
  onSign?: (signatureType: SignatureType) => void;
  /** Whether signature boxes are editable (true = can sign) */
  editable?: boolean;
  /** Callback when a signature is deleted (for parent to refresh) */
  onSignatureDeleted?: () => void;
  /** Order source label (e.g., "P360-Sub", "NEXI") to display instead of transfer status */
  orderSource?: string;
  /** Color for the order source label (MUI theme color or CSS color) */
  orderSourceColor?: string;
  /** Pre-loaded counsel checkbox flags (keyed by checkbox name) */
  counselFlags?: Record<string, boolean>;
  /** Callback when a counsel checkbox is toggled */
  onCounselFlagChange?: (flags: Record<string, boolean>) => void;
  /** Patient ID for adding notes tagged to this Rx */
  patientId?: string;
  /**
   * GP-1358: this Rx's counseling sessions, rendered as a block INSIDE the
   * printable body (so Print emits it in the hardcopy layout). Display-only —
   * build it with `toHardcopyCounselingEntries()` so the printed block and the
   * on-screen counseling card never drift.
   */
  counselingRecords?: HardcopyCounselingEntry[];
  /** PRX-51: photos covering product verification for this Rx (item or pack scope). */
  verificationPhotos?: VerificationPhoto[];
  /** PRX-51: opens a verification photo. Both this and `verificationPhotos` are
   * optional — every existing caller keeps working with no change and renders
   * nothing new until it starts passing them. */
  onOpenVerificationPhoto?: (photo: VerificationPhoto) => void;
}

/** One counseling session as it prints on the hardcopy (GP-1358). */
export interface HardcopyCounselingEntry {
  id: string;
  /** "Completed" / "Declined" — already resolved for display. */
  statusLabel: string;
  /** "by phone", "in person", … */
  method?: string;
  /** Counsellor's display name (auth0 ids must be resolved by the caller). */
  counselorName?: string;
  /**
   * The session's date-time, ALREADY formatted for display
   * (`toHardcopyCounselingEntries` owns the formatting — these are timestamptz
   * instants, and this component's `formatDate` is for Kroll's naive timestamps,
   * so routing them through it shifted the printed time by the UTC offset).
   */
  counselledAt?: string;
  /** Human-readable decline reason. */
  declinedReason?: string;
  /** "12/21" */
  checklistProgress?: string;
  comments?: string;
}

/**
 * PRX-51: the photo a signature box links to when an Rx has several — the
 * latest capture wins. A missing or unparseable `capturedAt` sorts oldest so a
 * row with no timestamp can never displace a real retake. Exported for the
 * spec; the array itself is never mutated.
 */
export function pickNewestPhoto(
  photos: VerificationPhoto[] | undefined,
): VerificationPhoto | undefined {
  if (!photos || photos.length === 0) return undefined;
  const at = (photo: VerificationPhoto): number => {
    const parsed = photo.capturedAt ? new Date(photo.capturedAt).getTime() : NaN;
    return Number.isFinite(parsed) ? parsed : -Infinity;
  };
  return photos.reduce((newest, candidate) => (at(candidate) >= at(newest) ? candidate : newest));
}

/**
 * SignatureBox - Individual signature box component
 */
interface SignatureBoxProps {
  label: string;
  type: SignatureType;
  signatures: HardcopySignature[];
  currentUserInitials?: string;
  currentUserEmail?: string;
  editable?: boolean;
  onSign?: (signatureType: SignatureType) => void;
  onDelete?: (signature: HardcopySignature) => void;
  compact?: boolean;
  /** PRX-51: photos covering this Rx. Only the product verification box uses them. */
  photos?: VerificationPhoto[];
  onOpenPhoto?: (photo: VerificationPhoto) => void;
}

const SignatureBox: React.FC<SignatureBoxProps> = ({
  label,
  type,
  signatures,
  currentUserInitials,
  currentUserEmail,
  editable = false,
  onSign,
  onDelete,
  compact = false,
  photos,
  onOpenPhoto,
}) => {
  // Find existing signature for this type
  const existingSignature = signatures.find((sig) => sig.signatureType === type);
  const isSigned = !!existingSignature;
  const displayInitials = existingSignature?.initials || '';

  // Check if current user can delete this signature
  const canDelete = isSigned && currentUserEmail && existingSignature?.userEmail === currentUserEmail;

  // PRX-51: the photo joins the signature rather than duplicating it — the box
  // already carries who signed and when in its tooltip. When an Rx has more
  // than one photo the NEWEST by capturedAt is shown: a retake is how a
  // technician corrects a mis-framed or unreadable frame, and only a pharmacy
  // admin can delete the bad one, so showing the first capture forever would
  // pin the hardcopy to the frame the retake was meant to replace. The badge
  // links to one photo, not a list.
  const photo = pickNewestPhoto(photos);

  const handleClick = () => {
    if (editable && !isSigned && onSign) {
      onSign(type);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the box click
    if (canDelete && onDelete && existingSignature) {
      onDelete(existingSignature);
    }
  };

  return (
    <Box
      onClick={handleClick}
      title={
        isSigned
          ? `Signed by ${existingSignature?.userName || 'Unknown'} on ${
              existingSignature?.signedAt
                ? format(new Date(existingSignature.signedAt), 'MMM dd, yyyy h:mm a')
                : 'Unknown date'
            }`
          : editable
          ? 'Click to sign'
          : ''
      }
      sx={{
        border: '1px solid #999',
        width: '30%',
        minHeight: compact ? 50 : 60,
        display: 'flex',
        flexDirection: 'column',
        cursor: editable && !isSigned ? 'pointer' : 'default',
        bgcolor: isSigned ? '#e8f5e9' : editable && !isSigned ? '#fff9c4' : 'white',
        transition: 'background-color 0.2s',
        '&:hover': editable && !isSigned ? { bgcolor: '#fff176' } : {},
        position: 'relative',
      }}
    >
      {/* Delete button (only for signed boxes by current user) */}
      {canDelete && onDelete && (
        <IconButton
          size="small"
          onClick={handleDelete}
          title="Remove signature"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            padding: 0.3,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              color: 'error.main',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: compact ? 12 : 14 }} />
        </IconButton>
      )}

      {/* PRX-51: camera badge — a photo taken at product verification. */}
      {photo && onOpenPhoto && (
        <IconButton
          size="small"
          aria-label="View product verification photo"
          title={describeCapture(photo)}
          onClick={(e) => {
            e.stopPropagation();
            onOpenPhoto(photo);
          }}
          sx={{ position: 'absolute', top: 2, left: 2, padding: 0.3 }}
        >
          <CameraAltIcon sx={{ fontSize: compact ? 12 : 14 }} />
        </IconButton>
      )}

      {/* Label */}
      <Box
        sx={{
          fontSize: hardcopyFontSize('micro', compact),
          color: '#666',
          textAlign: 'center',
          borderBottom: '1px solid #ddd',
          py: 0.3,
          bgcolor: '#f5f5f5',
        }}
      >
        {label}
      </Box>
      {/* Signature display area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 0.5,
        }}
      >
        {/* Fancy signature initials.
            GP-1406: a read-only box for an unsigned verification used to render
            as blank space, which reads exactly like "still loading" — the audit
            of TS-ORD-20260810-201622-546-1786394400539 could not tell an
            unverified order apart from an unfetched one. Say it in words. */}
        {isSigned || editable ? (
          <Box
            sx={{
              fontWeight: 700,
              fontSize: compact ? '18px' : '24px',
              color: isSigned ? '#1565c0' : '#999',
              fontFamily: "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive",
              fontStyle: 'italic',
              letterSpacing: '2px',
              textDecoration: isSigned ? 'underline' : 'none',
              textDecorationColor: '#1565c0',
              transform: 'rotate(-2deg)',
              textShadow: isSigned ? '1px 1px 1px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {isSigned ? displayInitials : currentUserInitials || '...'}
          </Box>
        ) : (
          <Box
            sx={{
              fontSize: hardcopyFontSize('micro', compact),
              color: '#8a8a8a',
              fontFamily: HARDCOPY_FONT_STACK,
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Not signed
          </Box>
        )}
        {/* Date/time for signed signatures */}
        {isSigned && existingSignature?.signedAt && (
          <Box
            sx={{
              fontSize: hardcopyFontSize('micro', compact),
              color: '#666',
              mt: 0.3,
              fontFamily: HARDCOPY_FONT_STACK,
            }}
          >
            {format(new Date(existingSignature.signedAt), 'MM/dd/yy h:mma')}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Props for {@link HardcopySignatures}. */
export interface HardcopySignaturesProps {
  signatures: HardcopySignature[];
  /** The Rx these boxes belong to — used to match an item-scoped photo. */
  rxNumber?: string | number | null;
  currentUserInitials?: string;
  currentUserEmail?: string;
  editable?: boolean;
  onSign?: (signatureType: SignatureType) => void;
  onDeleteSignature?: (signature: HardcopySignature) => void;
  compact?: boolean;
  /** Patient ID for adding notes tagged to this Rx. */
  patientId?: string;
  onAddNote?: () => void;
  /** PRX-51: photos covering product verification for this Rx, and how to open one. */
  verificationPhotos?: VerificationPhoto[];
  onOpenVerificationPhoto?: (photo: VerificationPhoto) => void;
}

/**
 * HardcopySignatures - the three verification signature boxes (data, clinical,
 * product) plus the "nothing signed yet" banner and the add-note affordance.
 *
 * Extracted out of {@link HardcopyViewer} (PRX-51) purely so the product
 * verification photo badge could be unit-tested without the whole hardcopy
 * fetch/print machinery — the JSX and behaviour are unchanged for every
 * existing caller.
 */
export const HardcopySignatures: React.FC<HardcopySignaturesProps> = ({
  signatures,
  rxNumber,
  currentUserInitials,
  currentUserEmail,
  editable = false,
  onSign,
  onDeleteSignature,
  compact = false,
  patientId,
  onAddNote,
  verificationPhotos,
  onOpenVerificationPhoto,
}) => {
  // GP-1406: has ANY of the three verification signatures been captured for this
  // Rx? Drives the explicit "no verification captured" line above the boxes.
  const hasAnyVerificationSignature = signatures.some((sig) =>
    VERIFICATION_SIGNATURE_TYPES.includes(sig.signatureType),
  );

  // PRX-51: a pack photo covers every Rx in the order; an item photo covers
  // only its own Rx. `evidence` on the completion event is authoritative for
  // WHAT was checked — this filter only decides which photo, if any, this
  // particular signature box links to.
  const productVerificationPhotos = (verificationPhotos ?? []).filter(
    (p) => p.scope === 'pack' || String(p.rxNumber ?? '') === String(rxNumber ?? ''),
  );

  return (
    <>
      {/* Signatures */}
      <Box sx={{ mt: 4, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Verification Signatures
        </Typography>
        {patientId && editable && onAddNote && (
          <Tooltip title="Add a note tagged to this Rx" arrow>
            <IconButton size="small" onClick={onAddNote} sx={{ color: 'primary.main' }}>
              <NoteAddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      {/* GP-1406: three blank boxes are not an answer. When NOTHING has been
          signed, say so above them — the cancelled Imiquimod order was shipped,
          SMS'd and carried on a Descartes shipment with all three empty, and the
          screen gave the auditor no way to read that as "never verified". */}
      {!hasAnyVerificationSignature && (
        <Box
          sx={{
            fontSize: hardcopyFontSize('emphasis', compact),
            fontFamily: HARDCOPY_FONT_STACK,
            color: '#242424',
            mb: 1,
          }}
        >
          No verification captured — this Rx has no data, clinical or product verification
          signature.
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <SignatureBox
          label="Data Verification"
          type="data_verification"
          signatures={signatures}
          currentUserInitials={currentUserInitials}
          currentUserEmail={currentUserEmail}
          editable={editable}
          onSign={onSign}
          onDelete={onDeleteSignature}
          compact={compact}
        />
        <SignatureBox
          label="Clinical Verification"
          type="clinical_verification"
          signatures={signatures}
          currentUserInitials={currentUserInitials}
          currentUserEmail={currentUserEmail}
          editable={editable}
          onSign={onSign}
          onDelete={onDeleteSignature}
          compact={compact}
        />
        <SignatureBox
          label="Product Verification"
          type="product_verification"
          signatures={signatures}
          currentUserInitials={currentUserInitials}
          currentUserEmail={currentUserEmail}
          editable={editable}
          onSign={onSign}
          onDelete={onDeleteSignature}
          compact={compact}
          photos={productVerificationPhotos}
          onOpenPhoto={onOpenVerificationPhoto}
        />
      </Box>
    </>
  );
};

/**
 * HardcopyViewer - Reusable component for displaying prescription hardcopy
 *
 * Self-contained component that fetches and displays hardcopy data for a prescription.
 * Can be used across different pages (Product Verification, Clinical Verification, etc.)
 */
export const HardcopyViewer: React.FC<HardcopyViewerProps> = ({
  rxNumber,
  pharmacyId: propPharmacyId,
  compact = false,
  onLoaded,
  onError,
  initialData,
  packagingData,
  onRxClick,
  signatures = [],
  currentUserInitials,
  currentUserEmail,
  currentUserName,
  onSign,
  editable = false,
  onSignatureDeleted,
  orderSource,
  orderSourceColor,
  counselFlags: propCounselFlags,
  onCounselFlagChange,
  patientId,
  counselingRecords,
  verificationPhotos,
  onOpenVerificationPhoto,
}) => {
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  const pharmacyId = propPharmacyId || activePharmacy?.id;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [hardcopyData, setHardcopyData] = useState<HardcopyData | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [signatureToDelete, setSignatureToDelete] = useState<HardcopySignature | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Counsel checkbox flags
  const [counselChecks, setCounselChecks] = useState<Record<string, boolean>>(propCounselFlags || {});
  useEffect(() => {
    if (propCounselFlags) setCounselChecks(propCounselFlags);
  }, [propCounselFlags]);

  const handleCounselToggle = (key: string) => {
    if (!editable) return;
    const updated = { ...counselChecks, [key]: !counselChecks[key] };
    if (key === 'didCounsel' && updated.didCounsel) updated.notCounsel = false;
    if (key === 'notCounsel' && updated.notCounsel) updated.didCounsel = false;
    setCounselChecks(updated);
    onCounselFlagChange?.(updated);
  };

  const fetchHardcopyData = useCallback(async () => {
    if (!rxNumber || !pharmacyId) {
      setError('Missing rxNumber or pharmacyId');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/rx/${pharmacyId}/hardcopy/${rxNumber}`);

      if (response.data.success) {
        setHardcopyData(response.data.data);
        onLoaded?.(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load hardcopy data');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load hardcopy data';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [rxNumber, pharmacyId, onLoaded, onError]);

  useEffect(() => {
    if (!initialData && rxNumber && pharmacyId) {
      fetchHardcopyData();
    }
  }, [initialData, rxNumber, pharmacyId, fetchHardcopyData]);

  // Handle signature deletion
  const handleDeleteClick = (signature: HardcopySignature) => {
    setSignatureToDelete(signature);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!signatureToDelete || !pharmacyId) return;

    setDeleting(true);
    try {
      const response = await api.delete(
        `/api/rx/${pharmacyId}/signatures/${signatureToDelete.id}`
      );

      if (response.data.success) {
        // Success - clear local state
        setDeleteDialogOpen(false);
        setSignatureToDelete(null);

        // Notify parent component to refresh signatures list
        if (onSignatureDeleted) {
          onSignatureDeleted();
        }
      } else {
        throw new Error(response.data.message || 'Failed to delete signature');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to delete signature';
      alert(`Error: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSignatureToDelete(null);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() || !patientId) return;
    setSavingNote(true);
    try {
      await notesService.createNoteForPanel({
        entityType: 'patient',
        entityId: patientId,
        content: noteText.trim(),
        author: { id: user?.sub || '', name: user?.name || '', email: user?.email || '' },
        type: 'clinical_note',
        tags: [`rx:${rxNumber}`, 'verification-signature', 'clinical-verification'],
        relatedPrescriptionId: rxNumber,
      });
      enqueueSnackbar('Note saved', { variant: 'success' });
      setNoteText('');
      setNoteDialogOpen(false);
    } catch (err) {
      enqueueSnackbar('Failed to save note', { variant: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  // Track if a print is currently in progress to prevent concurrent prints
  const isPrintingRef = React.useRef(false);

  // Handle print - uses iframe to preserve all original styles exactly
  const handlePrint = () => {
    // Guard against concurrent prints
    if (isPrintingRef.current) {
      return;
    }

    const hardcopyContent = document.querySelector('.hardcopy-printable-content') as HTMLElement;
    if (!hardcopyContent) {
      alert('Unable to find hardcopy content to print');
      return;
    }

    isPrintingRef.current = true;

    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      alert('Unable to create print frame');
      document.body.removeChild(iframe);
      isPrintingRef.current = false;
      return;
    }

    // Copy ALL stylesheets from the main document
    const styleSheets = Array.from(document.styleSheets);
    let styles = '';
    styleSheets.forEach(sheet => {
      try {
        if (sheet.cssRules) {
          Array.from(sheet.cssRules).forEach(rule => {
            styles += rule.cssText + '\n';
          });
        }
      } catch (e) {
        // Some stylesheets may be cross-origin and throw errors
        if (sheet.href) {
          styles += `@import url("${sheet.href}");\n`;
        }
      }
    });

    // Clone the content
    const clonedContent = hardcopyContent.cloneNode(true) as HTMLElement;
    clonedContent.querySelectorAll('.no-print').forEach(el => el.remove());

    // Write to iframe with all styles
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Hardcopy - Rx ${rxNumber}</title>
          <style>
            ${styles}
            /* Print-specific overrides */
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              body {
                margin: 0;
                padding: 15px;
                background: white !important;
              }
              .history-section {
                page-break-before: auto;
              }
            }
            /* Ensure content is visible. GP-1163: the printed hardcopy must use
               the same Verdana stack as the on-screen pane — this body rule was
               still forcing Courier New onto every sheet that came out of the
               printer, which is where the "blocky" complaint originated.
               This rule is load-bearing, not belt-and-braces: we clone the inner
               .hardcopy-printable-content div, NOT its <Paper>, so every property
               the sheet inherits from the Paper (family, base size, line-height)
               is lost in the iframe and has to be restated here. Per-element
               sizes survive because the cloned nodes keep their emotion classes
               and ${'${styles}'} above carries those rules over. */
            body {
              font-family: ${HARDCOPY_FONT_STACK};
              font-size: ${hardcopyFontSize('body', compact)};
              padding: 15px;
              background: white;
              line-height: ${HARDCOPY_LINE_HEIGHT};
            }
          </style>
        </head>
        <body>
          ${clonedContent.outerHTML}
        </body>
      </html>
    `);
    iframeDoc.close();

    // Cleanup function to remove iframe and reset state
    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      isPrintingRef.current = false;
    };

    // Wait for styles to load, then print
    setTimeout(() => {
      const iframeWindow = iframe.contentWindow;
      if (!iframeWindow) {
        cleanup();
        return;
      }

      // Setup afterprint handler before calling print
      const afterPrintHandler = () => {
        iframeWindow.removeEventListener('afterprint', afterPrintHandler);
        cleanup();
      };

      // Check if afterprint is supported
      if ('onafterprint' in iframeWindow) {
        iframeWindow.addEventListener('afterprint', afterPrintHandler);
      }

      iframeWindow.focus();
      iframeWindow.print();

      // Fallback timeout for browsers that don't support afterprint (e.g., some Safari versions)
      // Use longer timeout (10s) to ensure print dialog has closed
      if (!('onafterprint' in iframeWindow)) {
        setTimeout(cleanup, 10000);
      }
    }, 500);
  };

  // Format date helper - parses without timezone conversion
  const formatDate = (dateStr: string | null, formatStr: string = 'dd-MMM-yyyy'): string => {
    if (!dateStr) return 'N/A';
    try {
      // Strip timezone suffix (Z or +00:00) to treat as local time from Kroll
      const cleanDateStr = dateStr.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
      // Parse as local time by appending to create a date without TZ offset
      const [datePart, timePart] = cleanDateStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(n => parseInt(n, 10));
      const date = new Date(year, month - 1, day, hour, minute, second || 0);
      return format(date, formatStr);
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '0.00';
    return amount.toFixed(2);
  };

  const formatPackagingExpiry = (expiry?: string | null): string => {
    if (!expiry) return '';
    const trimmed = expiry.trim();

    // YYYY-MM-DD → DD/MM/YYYY (preserve the day)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-');
      return `${day}/${month}/${year}`;
    }

    // YYYY-MM → MM/YYYY (no day available)
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
      const [year, month] = trimmed.split('-');
      return `${month}/${year}`;
    }

    // Already in DD/MM/YYYY format - return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      return trimmed;
    }

    // Already in MM/YYYY or MM/YY format - return as-is
    if (/^\d{2}\/\d{4}$/.test(trimmed) || /^\d{2}\/\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    // Fallback: return raw value
    return trimmed;
  };

  /**
   * Calculate days between a date string (YYYY-MM-DD or ISO) and today
   * Uses local date components to avoid timezone issues per date-formatting-standards.md
   */
  const calculateDaysAgo = (dateStr: string | null): number | null => {
    if (!dateStr) return null;

    // Strip timezone suffix and parse date part
    const cleanDateStr = dateStr.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    const datePart = cleanDateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);

    // Create date as local midnight
    const prevDate = new Date(year, month - 1, day);

    // Get today as local midnight
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Calculate difference in days
    const diffTime = todayMidnight.getTime() - prevDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const normalizedPackagingData: PackagingSummary[] = useMemo(() => {
    const source: PackagingDataInput = packagingData ?? (hardcopyData as any)?.packagingData;
    if (!source) return [];

    const items = Array.isArray(source) ? source : [source];

    // Filter packaging items to only show data for the current prescription
    // For multi-Rx orders, this ensures lot/expiry data appears on the correct hardcopy
    const filteredItems = items.filter((item) => {
      if (!item) return false;

      // If item has rxNumber, only include if it matches current prescription
      if (item.rxNumber) {
        return String(item.rxNumber) === String(rxNumber);
      }

      // Backward compatibility: if no rxNumber in data, include it
      // (legacy single-Rx orders or old data without rxNumber field)
      return true;
    });

    const summaries: PackagingSummary[] = [];

    for (const item of filteredItems) {
      if (!item) continue;

      const scans = Array.isArray(item.scans) ? item.scans.filter(Boolean) : [];
      const packageType = item.packageType;

      if (scans.length > 0) {
        // Group scans by unique lot/expiry to show all packs
        const scanGroups = new Map<string, { lot: string; expiry: string; count: number }>();

        for (const scan of scans) {
          const lot = (scan.lotNumber || '').trim();
          const expiry = formatPackagingExpiry(scan.expiryDate) || '';
          if (!lot && !expiry) continue;
          const key = `${lot}||${expiry}`;
          const existing = scanGroups.get(key);
          if (existing) {
            existing.count += 1;
          } else {
            scanGroups.set(key, { lot, expiry, count: 1 });
          }
        }

        if (scanGroups.size > 0) {
          for (const group of scanGroups.values()) {
            summaries.push({
              lotNumber: group.lot || undefined,
              expiryDate: group.expiry || undefined,
              quantity: group.count,
              packageType,
            });
          }
        } else {
          // Scans exist but none had lot/expiry — fall back to item-level
          const lotNumber = (item.lotNumber || '').trim() || undefined;
          const expiryDate = formatPackagingExpiry(item.expiryDate) || undefined;
          if (lotNumber || expiryDate) {
            const rawQuantity = item.quantityPackaged ?? scans.length;
            summaries.push({
              lotNumber,
              expiryDate,
              quantity: typeof rawQuantity === 'string' ? Number(rawQuantity) || null : rawQuantity ?? null,
              packageType,
            });
          }
        }
      } else {
        // No scans — use item-level lot/expiry (legacy data)
        const lotNumber = (item.lotNumber || '').trim() || undefined;
        const expiryDate = formatPackagingExpiry(item.expiryDate) || undefined;
        if (lotNumber || expiryDate) {
          const rawQuantity = item.quantityPackaged;
          summaries.push({
            lotNumber,
            expiryDate,
            quantity: typeof rawQuantity === 'string' ? Number(rawQuantity) || null : rawQuantity ?? null,
            packageType,
          });
        }
      }
    }

    return summaries;
  }, [packagingData, hardcopyData, rxNumber]);

  // Total drug units actually packaged for this Rx. Sourced from the packaging
  // step's quantityPackaged (merged into metadata.packagingData by rxNumber), not
  // from Kroll. Rendered on the packaging section as "<qty> <drugForm>"
  // (e.g. "30 TAB (Tablet)") instead of the bottle/scan count + package type
  // ("1 | vial"), which confused verifiers. On a partial fill this is the amount
  // physically in the container (e.g. 30 of a 90 Rx), so it stays faithful to the
  // package contents rather than the full prescribed quantity.
  const packagedQty = useMemo<number | null>(() => {
    const source: PackagingDataInput = packagingData ?? (hardcopyData as any)?.packagingData;
    if (!source) return null;
    const items = Array.isArray(source) ? source : [source];
    let total = 0;
    let found = false;
    for (const item of items) {
      if (!item) continue;
      // Same per-Rx filter used for the lot/expiry rows below.
      if (item.rxNumber && String(item.rxNumber) !== String(rxNumber)) continue;
      const raw = item.quantityPackaged;
      const num = typeof raw === 'string' ? Number(raw) : raw;
      if (num !== undefined && num !== null && !Number.isNaN(num)) {
        total += num;
        found = true;
      }
    }
    return found ? total : null;
  }, [packagingData, hardcopyData, rxNumber]);

  // GP-1206: Days early / days late on a refill.
  //
  // Kroll's hardcopy flags whether a refill was processed before the previous
  // supply would have run out. Verifiers currently have to open the prior claim
  // to see this. We reconstruct it from data already on the hardcopy:
  //   expectedRunOut = previousFillDate + previousDaysSupply
  //   delta          = expectedRunOut − thisFillDate  (= prevDaysSupply − Ago)
  // A positive delta means the fill happened EARLY (filled too soon); negative
  // means LATE. `Ago` (fillDate − prevFillDate) comes from the backend. The
  // previous fill's own days-supply is preferred (from Fill History); we fall
  // back to this fill's days-supply when history isn't loaded, since a
  // maintenance drug is normally refilled at the same days-supply.
  //
  // GP-1362: the gate below is the FILL TYPE LABEL itself, not just "is there a
  // prevFillDate". Rx 1476110 (GSP London) printed
  // "First Fill / Transferred In" and "Refilled 29 days late" stacked on top of
  // each other: it has one orphaned prior dispense (Prev == First == 24-Mar-2026,
  // Rx 1396704, Ago 129) and Auth 200 = Rem 100 + Disp 100, so getFillTypeLabel
  // calls it a first fill while this memo happily measured 129 days against a
  // 100-day supply. A first fill has no refill cycle to be late against — the
  // prior dispense sat unfilled/inactive rather than running out — so the two
  // readings must come from ONE predicate and can never contradict again.
  const fillTypeLabel = useMemo(
    () =>
      hardcopyData
        ? getFillTypeLabel(
            hardcopyData.prevFillDate,
            hardcopyData.rxNumber,
            hardcopyData.origRxNum,
            hardcopyData.authQty,
            hardcopyData.remQty,
            hardcopyData.dispQty,
          )
        : null,
    [hardcopyData],
  );

  const refillTiming = useMemo<{ delta: number } | null>(() => {
    if (!hardcopyData) return null;
    // Only meaningful for refills (a first fill has no prior supply to compare).
    if (fillTypeLabel === 'First Fill') return null;
    if (!hardcopyData.prevFillDate) return null;
    const ago = hardcopyData.agoDays ?? calculateDaysAgo(hardcopyData.prevFillDate);
    if (ago === null || ago === undefined) return null;
    const prevDaysSupply =
      hardcopyData.history?.find(
        (h) => String(h.rxNumber) === String(hardcopyData.prevRxNum),
      )?.daysSupply ?? hardcopyData.daysSupply;
    if (!prevDaysSupply || prevDaysSupply <= 0) return null;
    return { delta: prevDaysSupply - ago };
  }, [hardcopyData, fillTypeLabel]);

  // GP-1163: the ten-column Fill History grid has always been wider than a narrow
  // Clinical/Product Verification pane (measured 508px min-content against a 380px
  // pane BEFORE this ticket), so that pane has always scrolled horizontally. Going
  // from 13px Courier to 14px Verdana pushed it to 550px. Rather than shrink the
  // type back — the whole point of the ticket — buy the 42px back from the cells'
  // horizontal padding, which costs nothing legibility-wise: 10 columns x 2 sides
  // x 2px = 40px. Vertical padding (row rhythm) is untouched, and full mode keeps
  // the roomier 4px.
  const historyCellPadding = compact ? '4px 2px' : '4px';

  // GP-1039: previous-fill vs current-fill product, when the drug file was
  // switched under the same Rx lineage (see getDrugChange).
  const drugChange = useMemo(
    () => (hardcopyData ? getDrugChange(hardcopyData) : null),
    [hardcopyData],
  );

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: compact ? 200 : 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state - with retry button
  if (error) {
    return (
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchHardcopyData}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  // No data state
  if (!hardcopyData) {
    return <Alert severity="info">No hardcopy data available for Rx #{rxNumber}</Alert>;
  }

  // GP-1293: quantity + dosage-form abbreviation + drug name + strength, e.g.
  // "3 PEN Ajovy Autoinjector 225mg/1.5ml". Parts Kroll doesn't have (no form on
  // a compound, no strength) are dropped rather than printed as blanks.
  const drugLine = [
    hardcopyData.dispQty,
    getDrugFormAbbreviation(hardcopyData.drugForm),
    hardcopyData.brandName || hardcopyData.genericName,
    hardcopyData.drugStrength,
  ]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== '')
    .join(' ');

  // GP-1293: Kroll's physical-appearance description (Drg.Description), the same
  // value the Packaging step shows as "Description".
  const drugDescription = (hardcopyData.drugDescription || '').trim();

  // GP-1039: the two lines of the drug-change callout, each built as ONE string —
  // TamperResistantText takes a single text child so its MutationObserver can
  // restore the exact value if an extension/translator rewrites a drug name.
  const oldDrugLine = drugChange
    ? [
        `Old drug: ${drugChange.previous}`,
        drugChange.previousDIN ? ` (DIN ${drugChange.previousDIN})` : '',
        hardcopyData.lastFillDate
          ? ` — filled ${formatDate(hardcopyData.lastFillDate, 'dd-MMM-yyyy')}`
          : '',
        hardcopyData.lastFillRxNum ? ` (Rx ${hardcopyData.lastFillRxNum})` : '',
      ].join('')
    : '';
  const thisFillLine = drugChange
    ? `This fill: ${drugChange.current}${
        drugChange.currentDIN ? ` (DIN ${drugChange.currentDIN})` : ''
      }`
    : '';

  // Render hardcopy
  return (
    <Paper
      sx={{
        p: compact ? 1.5 : 3,
        maxWidth: compact ? '100%' : 700,
        mx: 'auto',
        border: 2,
        borderColor: '#000',
        // GP-1163: Verdana, not Courier New — see HARDCOPY_FONT_STACK.
        fontFamily: HARDCOPY_FONT_STACK,
        // A hardcopy is a paper facsimile: it stays white with dark ink in BOTH
        // themes (like PDFs / Rx images). Forcing the ink color here keeps all
        // inherited text readable when the app theme is dark.
        bgcolor: 'white',
        color: '#212529',
        fontSize: hardcopyFontSize('body', compact),
        lineHeight: HARDCOPY_LINE_HEIGHT,
        position: 'relative',
        '@media print': {
          border: 2,
          borderColor: '#000',
          boxShadow: 'none',
        },
      }}
    >
      {/* Print Button - Hidden during print */}
      <Box
        className="no-print"
        sx={{
          position: 'absolute',
          top: compact ? 8 : 16,
          right: compact ? 8 : 16,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Tooltip title="Print Hardcopy">
          <IconButton
            onClick={handlePrint}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Printable Content Wrapper — translate/spellcheck/Grammarly opt-outs
          guard drug names, DIN, strength, lot, expiry from being rewritten by
          browser extensions or built-in translators (patient-safety guard). */}
      <div
        className="hardcopy-printable-content"
        translate="no"
        lang="en"
        spellCheck={false}
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
      >
        {/* Patient Section - Light Yellow Background (matches Counseling panel) */}
      <Box sx={{ bgcolor: '#fffde7', borderRadius: 0.5, p: 0.8, mb: 0.8 }}>
      {/* Header - Rx Number and Fill Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
        <Box sx={{ fontWeight: 700, fontSize: hardcopyFontSize('identity', compact) }}>
          Rx:{hardcopyData.rxNumber}
        </Box>
        <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('emphasis', compact), pr: compact ? 5 : 6 }}>
          <div style={{ fontWeight: 600 }}>
            {formatDate(hardcopyData.fillDate, 'EEE dd-MMM-yyyy h:mm a')}
          </div>
          {hardcopyData.writtenDate && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>
              Written: {formatDate(hardcopyData.writtenDate, 'dd-MMM-yyyy')}
            </div>
          )}
        </Box>
      </Box>

      {/* Patient Name and Time — GP-1206: name is a primary scan target, set
          larger/bolder than surrounding detail (mirrors Kroll hierarchy). */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Box sx={{ fontWeight: 800, fontSize: hardcopyFontSize('identity', compact) }}>
          {hardcopyData.patientName}
        </Box>
        <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('body', compact) }}>
          {hardcopyData.userInit && <span>[{hardcopyData.userInit}]</span>}
          {hardcopyData.userInit && hardcopyData.rxInit && ' '}
          {hardcopyData.rxInit && <span>[{hardcopyData.rxInit}]</span>}
        </Box>
      </Box>

      {/* Patient Address and Phone */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
          {hardcopyData.patientAddress}
          {hardcopyData.patientAddress2 && `, ${hardcopyData.patientAddress2}`}
        </Box>
        <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('body', compact) }}>
          {hardcopyData.patientPhone || ''}
        </Box>
      </Box>

      {/* Patient City/Province/Postal and Sex */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
          {hardcopyData.patientCity}
          {hardcopyData.patientProvince && `, ${hardcopyData.patientProvince}`} {hardcopyData.patientPostalCode}
        </Box>
        <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('body', compact) }}>
          {hardcopyData.patientSex}
        </Box>
      </Box>

      {/* Patient DOB */}
      <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('body', compact), mb: 0 }}>
        DOB: {formatDate(hardcopyData.patientDOB, 'dd-MMM-yyyy')}
        {hardcopyData.patientDOB && (() => {
          // Age accounting for whether the birthday has occurred yet this year
          // (year subtraction alone over-counts before the birthday).
          const dob = hardcopyData.patientDOB
            .replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '').split('T')[0];
          const [by, bm, bd] = dob.split('-').map((n) => parseInt(n, 10));
          if (!by || !bm || !bd) return '';
          const now = new Date();
          let age = now.getFullYear() - by;
          const monthDiff = (now.getMonth() + 1) - bm;
          if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < bd)) age -= 1;
          return `(${age})`;
        })()}
      </Box>

      {/* GP-814: PI plan ClientID (BC PharmaCare / Pharmanet — also the BC PHN) */}
      {hardcopyData.piClientId && hardcopyData.piClientId.trim() !== '' && (
        <Box sx={{ fontSize: hardcopyFontSize('body', compact), mb: 0 }}>
          PI Client ID: {hardcopyData.piClientId}
        </Box>
      )}
      </Box>

      {/* Drug Info - Green Highlighted Box (matches Counseling panel) */}
      <Box sx={{ bgcolor: '#e8f5e9', p: 1, mb: 0.5, borderTop: 1, borderBottom: 1, borderColor: '#000' }}>
        {/* GP-1206: medication is a primary scan target — set larger/bolder.
            GP-1293: dosage form abbreviation sits between quantity and name. */}
        <TamperResistantText
          as="div"
          testId="hardcopy-drug-line"
          style={{ fontWeight: 800, fontSize: hardcopyFontSize('primary', compact) }}
        >
          {drugLine}
        </TamperResistantText>
        {hardcopyData.brandName && hardcopyData.genericName && hardcopyData.brandName !== hardcopyData.genericName && (
          <TamperResistantText
            as="div"
            style={{ fontSize: hardcopyFontSize('body', compact), fontStyle: 'italic' }}
          >
            {`(${hardcopyData.genericName})`}
          </TamperResistantText>
        )}
        {(hardcopyData.isMixture === 1 || hardcopyData.isMixture === true) && (
          <div style={{ fontSize: hardcopyFontSize('detail', compact), fontStyle: 'italic', color: '#555' }}>
            Compound / Mixture
          </div>
        )}
      </Box>

      {/* DIN and drug description — GP-1293: the space to the right of the DIN was
          empty; it now carries Kroll's physical-appearance description (the same
          field the Packaging step surfaces as "Description"), so a verifier can
          match the product against the hardcopy without opening Packaging. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.3 }}>
        <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 600, flexShrink: 0 }}>
          {hardcopyData.drugDIN
            ? `DIN: ${hardcopyData.drugDIN}`
            : (hardcopyData.isMixture === 1 || hardcopyData.isMixture === true)
              ? 'DIN: N/A (Compound)'
              : 'DIN: N/A'}
        </Box>
        {drugDescription && (
          <Box
            data-testid="hardcopy-drug-description"
            sx={{
              fontSize: hardcopyFontSize('body', compact),
              textAlign: 'right',
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {drugDescription}
          </Box>
        )}
      </Box>

      {/* GP-1039: Drug changed since the last fill — Kroll's "Old Drug" line.
          A drug file switched on an ACTIVE Rx (no new script) is invisible
          everywhere else on this hardcopy: the Rx number, prescriber, SIG and
          Orig Rx are all unchanged, so an accidental switch (Ozempic syringes →
          pens, Humira → citrate-free) used to reach the patient unnoticed.
          Printed directly under the DIN, where Kroll prints it, and styled as a
          hard warning because it is a patient-safety stop. */}
      {drugChange && (
        <Box
          sx={{
            mb: 0.8,
            border: 1,
            borderColor: '#c62828',
            borderLeftWidth: 4,
            borderRadius: 0.5,
            bgcolor: '#ffebee',
            px: 0.8,
            py: 0.6,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 800,
              fontSize: hardcopyFontSize('body', compact),
              letterSpacing: '0.5px',
              color: '#8e0000',
              textTransform: 'uppercase',
              mb: 0.3,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: compact ? 13 : 15 }} />
            Drug changed since last fill
          </Box>
          <TamperResistantText
            as="div"
            style={{ fontSize: hardcopyFontSize('body', compact), color: '#3e0000' }}
          >
            {oldDrugLine}
          </TamperResistantText>
          <TamperResistantText
            as="div"
            style={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700, color: '#3e0000' }}
          >
            {thisFillLine}
          </TamperResistantText>
        </Box>
      )}

      {/* Quantity and Drug Form */}

      {/* Quantity and Drug Form — GP-1293: kept as Kroll's full form string
          ("(3) PEN (Injection)"); the abbreviation alone now also leads the drug
          line above. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Box data-testid="hardcopy-drug-form" sx={{ fontSize: hardcopyFontSize('body', compact) }}>
          {/* Pack size, NOT DispQty (GSP 2026-08-28): the header already shows the
              dispensed quantity; printing it again here hid the billing math. With
              the pack size a verifier reads 180 dispensed / (60) = 3 inhalers at a
              glance. No fallback to DispQty when packSize is absent - repeating the
              quantity is exactly the misleading display this replaces. */}
          {hardcopyData.packSize != null && Number(hardcopyData.packSize) > 0
            ? `(${hardcopyData.packSize}) `
            : ''}{hardcopyData.drugForm || ''}
        </Box>
        {hardcopyData.productSelection != null && (
          <Box sx={{ textAlign: 'right', fontSize: hardcopyFontSize('body', compact) }}>
            Prod Sel: {hardcopyData.productSelection}
          </Box>
        )}
      </Box>

      {/* Manufacturer and Days Supply */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2 }}>
        <Box></Box>
        <Box sx={{ textAlign: 'right' }}>
          {hardcopyData.oralWritten && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>O/W: {getOralWrittenLabel(hardcopyData.oralWritten)}</div>
          )}
          {hardcopyData.drugManufacturer && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>Mfr: {hardcopyData.drugManufacturer}</div>
          )}
          <div style={{ fontSize: hardcopyFontSize('body', compact) }}>Days: {hardcopyData.daysSupply}</div>
        </Box>
      </Box>

      {/* LU Code Display — GP-1206: bolded + highlighted so the limited-use
          fill-by limit is unmissable (mirrors the Kroll hardcopy, where the LU
          code prints bolded under the charge tier). A technician must not be
          able to fill past the LU expiry without noticing.
          GP-1259: rendered directly under the DIN line so the LU/ODB datum is
          grouped with the drug adjudication block it belongs to, above the
          First Fill / pricing section (was floating below the doctor box). */}
      {hardcopyData.luCode && hardcopyData.luCode.trim() !== '' && (
        <Box
          sx={{
            mb: 0.8,
            border: 1,
            borderColor: '#e6a700',
            borderLeftWidth: 4,
            borderRadius: 0.5,
            bgcolor: '#fff3b0',
            px: 0.8,
            py: 0.5,
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.8,
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 800, fontSize: hardcopyFontSize('emphasis', compact), letterSpacing: '0.5px' }}
          >
            LU CODE: {hardcopyData.luCode}
          </Box>
          {hardcopyData.luExpiryDate && (
            <Box
              component="span"
              sx={{ fontWeight: 800, fontSize: hardcopyFontSize('emphasis', compact), color: '#8a2b00' }}
            >
              Exp: {formatDate(hardcopyData.luExpiryDate, 'dd-MMM-yyyy')}
            </Box>
          )}
        </Box>
      )}

      {/* Packaging Lot/Expiry */}
      {normalizedPackagingData.length > 0 && (
        <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: '#000', py: 0.8, mb: 1 }}>
          {/* Header: section label + packaged quantity & drug form. Shows the
              amount actually packaged (e.g. "30 TAB (Tablet)") rather than the
              bottle/scan count + package type. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 1,
              mb: 0.5,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: hardcopyFontSize('emphasis', compact) }}>
              Packaging Lot / Expiry
            </div>
            {(packagedQty !== null || hardcopyData.drugForm) && (
              <div style={{ fontWeight: 700, fontSize: hardcopyFontSize('body', compact) }}>
                {packagedQty !== null ? packagedQty : ''}
                {packagedQty !== null && hardcopyData.drugForm ? ' ' : ''}
                {hardcopyData.drugForm || ''}
              </div>
            )}
          </Box>
          {normalizedPackagingData.map((item, index) => (
            <Box
              key={`${item.lotNumber || 'lot'}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.2,
                fontSize: hardcopyFontSize('body', compact),
              }}
            >
              <div>Lot: {item.lotNumber || 'N/A'}</div>
              <div>Exp: {item.expiryDate || 'N/A'}</div>
            </Box>
          ))}
        </Box>
      )}

      {/* Doctor Info - Light Purple Background (matches Counseling panel)
          GP-553: Was a relative parent with an absolute-positioned right
          column. The right column (Doc#, CHARGED, Copied, Due, FILL TYPE,
          Active badge) was taller than the doctor-info text on the left, so
          the bottom of the column — including the green Active badge —
          overflowed downward into the SIG box and visually blocked the
          frequency text ("EVERY 4 weeks"). Switching to a flex row makes
          both columns contribute to the parent's height naturally. */}
      <Box sx={{ borderTop: 1, borderColor: '#000', pt: 0.8, mb: 0.8, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, bgcolor: '#f3e5f5', borderRadius: 0.5, px: 0.8, pb: 0.8 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* GP-1206: prescriber name is a primary scan target — larger/bolder. */}
          <div style={{ fontWeight: 800, fontSize: hardcopyFontSize('emphasis', compact) }}>
            {hardcopyData.doctorName}
          </div>
          {hardcopyData.doctorAddress && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>{hardcopyData.doctorAddress}</div>
          )}
          {hardcopyData.doctorCity && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>
              {hardcopyData.doctorCity}
              {hardcopyData.doctorProvince && `, ${hardcopyData.doctorProvince}`}
            </div>
          )}
          <div style={{ fontSize: hardcopyFontSize('body', compact) }}>
            Phone: {hardcopyData.doctorPhone || 'N/A'}
          </div>
        </Box>

        {/* Right side of Doctor Info — now in normal flow alongside the left
            column so the parent box always contains the Active badge. */}
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: hardcopyFontSize('body', compact) }}>Doc# {hardcopyData.doctorLicence}</div>
          <div style={{ fontWeight: 700, fontSize: hardcopyFontSize('emphasis', compact) }}>{hardcopyData.isCharged}</div>
          {hardcopyData.copiedRxNum && (
            <div style={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>Copied: {hardcopyData.copiedRxNum}</div>
          )}
          <div style={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            Due: {formatDate(hardcopyData.dueDate, 'dd-MMM-yyyy HH:mm')}
          </div>
          {hardcopyData.lastFillDate && (
            <div style={{ fontSize: hardcopyFontSize('body', compact) }}>
              Last fill: {formatDate(hardcopyData.lastFillDate, 'dd-MMM-yyyy')}
              {hardcopyData.lastFillRxNum ? ` (${hardcopyData.lastFillRxNum})` : ''}
            </div>
          )}
          <div style={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            {fillTypeLabel?.toUpperCase()}
          </div>
          <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
            {/* Kroll status parity (2026-08-28): flag any fill whose Kroll
                adjudication state is not clean ("Pending Adj", "Needs Review",
                third party down, no response). Orthogonal to the status badge -
                these fills usually still read Active - and to INACTIVE. */}
            <RxAdjStateBadge adjState={hardcopyData.rxAdjState} size="small" />
            {/* Kroll prints INACTIVE when Rx.Inactive is set (e.g. this fill was
                copied to a newer Rx), regardless of the numeric Status. */}
            {(hardcopyData.inactive === true || hardcopyData.inactive === 1) ? (
              <Box
                component="span"
                sx={{
                  px: 1, py: 0.25, borderRadius: 1,
                  fontSize: hardcopyFontSize('detail', compact), fontWeight: 700,
                  bgcolor: '#9e9e9e', color: 'white',
                }}
              >
                INACTIVE
              </Box>
            ) : typeof hardcopyData.status === 'number' ? (
              <RxStatusBadge status={hardcopyData.status} size="small" />
            ) : (
              // PRX-71: Fillware carries no usable numeric status — render the
              // PMS gate verdict instead of ever falling through to "Unknown".
              (() => {
                const d = describePmsGate(hardcopyData.pmsGate);
                return <Chip size="small" color={d.color} label={d.label} data-testid="hardcopy-pms-gate-chip" />;
              })()
            )}
          </Box>
        </Box>
      </Box>

      {/* Prescriber Note — the prescriber's free-text comment from Kroll
          (Rx.DocCom / doctorComment). Pulled out of the doctor box's
          right-hand status column (where long notes overflowed and collided
          with the doctor name/address) into its own full-width amber callout.
          Text is shown verbatim — patient-safety rule, never alter prescriber
          wording — but word-wraps cleanly and stays visually distinct from the
          structured Doc#/Due/status fields.

          GP-1020: Burnaby-only. Alberta prescribers dispensing into BC record
          their real out-of-province licence in DocCom, so it must print there.
          Everywhere else DocCom holds unrelated free text (assistant names,
          email preferences) that was leaking onto every site's hardcopies —
          so the block is gated by the pharmacy-level feature flag. */}
      {isPrescriberNoteEnabled(pharmacyId) && hardcopyData.doctorComment && hardcopyData.doctorComment.trim() !== '' && (
        <Box
          sx={{
            mb: 0.8,
            border: 1,
            borderColor: '#e6a700',
            borderLeftWidth: 4,
            borderRadius: 0.5,
            bgcolor: '#fff8e1',
            px: 0.8,
            py: 0.6,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 700,
              fontSize: hardcopyFontSize('detail', compact),
              letterSpacing: '0.5px',
              color: '#8a6100',
              textTransform: 'uppercase',
              mb: 0.3,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: compact ? 12 : 14 }} />
            Prescriber Note
          </Box>
          <Box
            sx={{
              fontSize: hardcopyFontSize('body', compact),
              color: '#3e2c00',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {hardcopyData.doctorComment}
          </Box>
        </Box>
      )}

      {/* Central Fill Warning */}
      {!!hardcopyData.isCF && (
        <Box sx={{ textAlign: 'right', mb: 1.2 }}>
          <div style={{ color: 'red', fontWeight: 700, fontSize: hardcopyFontSize('emphasis', compact) }}>NEW RX</div>
          <div style={{ color: 'red', fontSize: hardcopyFontSize('body', compact) }}>No Script Image</div>
          <div style={{ color: 'red', fontWeight: 700, fontSize: hardcopyFontSize('emphasis', compact) }}>REPRINT</div>
        </Box>
      )}

      {/* SIG Box - Light Blue Background (matches Counseling panel).
          GP-1358: the reference rendering of a prescription's directions — every
          other surface must agree with it, so it is addressable in tests. */}
      <Box data-testid="hardcopy-sig" sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2, bgcolor: '#e3f2fd', borderRadius: 0.5 }}>
        <div style={{ fontWeight: 600, fontSize: hardcopyFontSize('emphasis', compact) }}>
          {hardcopyData.sig || 'TAKE AS DIRECTED'}
        </div>
      </Box>

      {/* PRX-25: Fillware per-Rx notes — directions notes and pharmacist notes
          Fillware keeps against this prescription. Absent on Kroll payloads. */}
      {hardcopyData.rxNotes && hardcopyData.rxNotes.length > 0 && (
        <Box data-testid="hardcopy-rx-notes" sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2, bgcolor: '#fff8e1', borderRadius: 0.5 }}>
          <div style={{ fontWeight: 700, fontSize: hardcopyFontSize('body', compact), marginBottom: 4 }}>Rx Notes</div>
          {hardcopyData.rxNotes.map((note) => (
            <div key={note.id} style={{ fontSize: hardcopyFontSize('body', compact), marginBottom: 4, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 600 }}>{note.noteTypeLabel}</span>
              {note.createdBy ? ` · ${note.createdBy}` : ''}
              {note.changed ? ` · ${new Date(note.changed).toLocaleDateString()}` : ''}
              {': '}
              {note.content}
            </div>
          ))}
        </Box>
      )}
      {/* Pricing Box */}
      <Box sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2, bgcolor: '#f5f5f5' }}>
        {/* T036/T037: Fill Type, Auth, Remaining with unlimited refills handling */}
        {/* Orig Rx / Auth / Rem line (matches Kroll layout) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            {/* T036: Show First Fill or Refill based on history.
                GP-1362: same memo that gates the refill-timing chip below. */}
            {fillTypeLabel}
            {/* T198: Show order source or Transfer Status indicator */}
            {' / '}
            <Box
              component="span"
              sx={{
                color: orderSource
                  ? (orderSourceColor || '#1976d2')
                  : (hardcopyData.isTransferIn === 1 || hardcopyData.isTransferIn === true ? '#1976d2' : '#4caf50'),
                fontWeight: 700,
              }}
            >
              {orderSource || getTransferStatusLabel(hardcopyData.isTransferIn)}
            </Box>
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            {/* T037: Handle unlimited refills for Auth */}
            Auth:{getRefillsDisplay(hardcopyData.remQty, hardcopyData.authQty, hardcopyData.unlimitedRefills).authorized}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            {/* T037: Handle unlimited refills for Rem */}
            Rem:{getRefillsDisplay(hardcopyData.remQty, hardcopyData.authQty, hardcopyData.unlimitedRefills).remaining}
          </Box>
        </Box>

        {/* GP-1206: Refill timing flag — how many days early / late this refill
            was processed relative to when the previous supply would run out.
            Early fills are the risk signal (processed too soon), so they're
            flagged red; late is muted; on-schedule is green.
            GP-1259: sits directly under the First Fill / On-Demand heading so
            the refill-timing datum is grouped with the fill section it belongs
            to (was lower down, below the Prev/Ago/First history line). */}
        {refillTiming && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mb: 0.3,
              px: 0.8,
              py: 0.25,
              borderRadius: 1,
              fontSize: hardcopyFontSize('body', compact),
              fontWeight: 800,
              color: 'white',
              bgcolor:
                refillTiming.delta > 0 ? '#c62828' : refillTiming.delta < 0 ? '#616161' : '#2e7d32',
            }}
          >
            {refillTiming.delta > 0
              ? `Refilled ${refillTiming.delta} day${refillTiming.delta === 1 ? '' : 's'} early`
              : refillTiming.delta < 0
              ? `Refilled ${Math.abs(refillTiming.delta)} day${Math.abs(refillTiming.delta) === 1 ? '' : 's'} late`
              : 'Refilled on schedule'}
          </Box>
        )}

        {/* Orig Rx line (matches Kroll layout) */}
        {hardcopyData.origRxNum && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.3, gap: 3 }}>
            <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
              Orig Rx: {hardcopyData.origRxNum}
            </Box>
          </Box>
        )}

        {/* History Line: Prev / Ago / First — wraps for the same reason (GP-1163). */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, flexWrap: 'wrap', columnGap: 1 }}>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
            Prev:{hardcopyData.prevFillDate ? `${formatDate(hardcopyData.prevFillDate)}(${hardcopyData.prevRxNum})` : 'N/A'}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            {/* Ago = days between this fill and the previous fill (fillDate − prevFillDate),
                from the backend; not days-since-today. Matches Kroll's "Ago". */}
            Ago:{hardcopyData.agoDays ?? calculateDaysAgo(hardcopyData.prevFillDate) ?? 'N/A'}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
            First:{hardcopyData.firstFillDate ? `${formatDate(hardcopyData.firstFillDate)}(${hardcopyData.firstRxNum})` : 'N/A'}
          </Box>
        </Box>

        {/* Cost Breakdown */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>Cost:${(hardcopyData.cost || 0).toFixed(2)}</Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>Mkup:${(hardcopyData.markup || 0).toFixed(2)}</Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>Fee:${(hardcopyData.fee || 0).toFixed(2)}</Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            Total:${(hardcopyData.retailPrice || 0).toFixed(2)}
          </Box>
        </Box>

        {/* Patient Pays — GP-1163: wraps like the Cost row above, since Verdana is
            wider than the Courier New this box was laid out in. */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, flexWrap: 'wrap', columnGap: 1 }}>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            Pat:${(hardcopyData.customerPrice || 0).toFixed(2)}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            GP%:{hardcopyData.grossProfitPercent == null ? 'unavailable' : formatCurrency(hardcopyData.grossProfitPercent)}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            Disc:{formatCurrency(hardcopyData.totalDiscount)}
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
            Old Price:{(hardcopyData.netPrice || 0).toFixed(2)}
          </Box>
        </Box>

        {/* Insurance Pays */}
        <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontWeight: 700 }}>
          T.P.: ${(hardcopyData.insurancePays || 0).toFixed(2)} - {hardcopyData.thirdPartyBreakdown}
        </Box>
      </Box>

      {/* PRX-165 WP-23: Fillware claims that could not be read are said so,
          never left looking like a fill with no plan. */}
      {hardcopyData.adjudicationsUnavailable && (
        <Box sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2 }}>
          <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700 }}>
            Adjudication unavailable
          </Box>
          <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
            The claims for this fill could not be read. Check them in Fillware.
          </Box>
        </Box>
      )}

      {/* GP-1006: Adjudication — submitted intervention codes + third-party response.
          Codes verbatim from Kroll (RxPln/RxPlnAdj); no description lookup exists. */}
      {Array.isArray(hardcopyData.adjudications) && hardcopyData.adjudications.length > 0 && (
        <Box sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2 }}>
          {/* GP-1163: adjudication result / response codes are the sheet's
              warnings — set at the 12pt emphasis tier, not body. */}
          <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700, mb: 0.4 }}>
            Adjudication
          </Box>
          {hardcopyData.adjudications.map((adj) => {
            const messageLines = (adj.messages || '')
              .split(/\r\n|\r|\n/)
              .map((line) => line.trim())
              .filter(Boolean);
            return (
              <Box key={`${adj.planCode}-${adj.seq}`} sx={{ mb: 0.6, '&:last-child': { mb: 0 } }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.2 }}>
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700 }}>
                    {/* PRX-165 WP-23: Fillware sends the plan name, and a BIN for a
                        rejected plan taken off the fill. Kroll sends neither. */}
                    {adj.planCode || (adj.bin ? `BIN ${adj.bin}` : '')}
                    {adj.planName ? ` · ${adj.planName}` : ''}
                    {adj.tranType === 3 ? ' (Rej)' : ''}
                    {adj.claimStatus === 'reversed' ? ' (Reversed)' : ''}
                  </Box>
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>
                    Result: {adj.resultCode || 'N/A'}
                  </Box>
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>
                    Int: {adj.interventionCodes || adj.interventionsSubmitted || 'None'}
                  </Box>
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>
                    Resp: {adj.responseCodes || 'None'}
                  </Box>
                  {/* PRX-165 WP-23b: a reversed Fillware claim paid nothing, so no
                      amount is shown, as on the payment step. */}
                  {adj.claimStatus !== 'reversed' && (
                    <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700 }}>
                      {adj.planPays == null ? 'Paid: unavailable' : `Paid: $${(Number(adj.planPays) || 0).toFixed(2)}`}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, color: '#444' }}>
                  {adj.traceNum && (
                    <Box sx={{ fontSize: hardcopyFontSize('detail', compact) }}>Trace: {adj.traceNum}</Box>
                  )}
                  {adj.refNum && (
                    <Box sx={{ fontSize: hardcopyFontSize('detail', compact) }}>Ref: {adj.refNum}</Box>
                  )}
                  {adj.adjResponseDate && (
                    <Box sx={{ fontSize: hardcopyFontSize('detail', compact) }}>{adj.adjResponseDate}</Box>
                  )}
                </Box>
                {messageLines.length > 0 && (
                  <Box sx={{ mt: 0.2 }}>
                    {messageLines.map((line, idx) => (
                      <Box
                        key={idx}
                        sx={{ fontSize: hardcopyFontSize('body', compact), fontStyle: 'italic' }}
                      >
                        {line}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Bottom Checkboxes — Interactive when editable */}
      <Box sx={{ borderTop: 1, borderColor: '#000', pt: 1, mb: 1 }}>
        <Grid container spacing={1}>
          {[
            { key: 'didCounsel', label: 'DID Counsel' },
            { key: 'inPerson', label: 'In Person' },
            { key: 'viaPhone', label: 'Via Phone' },
            { key: 'agent', label: 'Agent' },
            { key: 'notCounsel', label: 'NOT Counsel' },
            { key: 'patRefused', label: 'Pat Refused' },
            { key: 'language', label: 'Language' },
            { key: 'delivered', label: 'Delivered' },
          ].map((item) => (
            <Grid
              item xs={3} key={item.key}
              sx={{
                fontSize: hardcopyFontSize('detail', compact),
                cursor: editable ? 'pointer' : 'default',
                userSelect: 'none',
                '&:hover': editable ? { bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 0.5 } : {},
              }}
              onClick={() => handleCounselToggle(item.key)}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  border: '1px solid #000',
                  mr: 0.5,
                  position: 'relative',
                  bgcolor: counselChecks[item.key] ? '#1976d2' : 'white',
                  borderColor: counselChecks[item.key] ? '#1976d2' : '#000',
                  verticalAlign: 'middle',
                  ...(counselChecks[item.key] && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 2,
                      top: 0,
                      width: 5,
                      height: 8,
                      border: 'solid white',
                      borderWidth: '0 2px 2px 0',
                      transform: 'rotate(45deg)',
                    },
                  }),
                }}
              />
              {item.label}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* GP-1358: Counseling — the sessions recorded against THIS Rx, printed
          with the hardcopy. The counsel checkboxes above say only that
          counselling happened; who did it, when, how, and how much of the ISEC
          checklist was covered lived one tab away in RX History and never made
          it onto the printed sheet. Inside the printable body, so Print emits
          it in the hardcopy layout. */}
      {Array.isArray(counselingRecords) && counselingRecords.length > 0 && (
        <Box className="counseling-section" sx={{ border: 1, borderColor: '#000', p: 1.2, mb: 1.2 }}>
          <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700, mb: 0.4 }}>
            Counseling
          </Box>
          {counselingRecords.map((rec) => (
            <Box key={rec.id} sx={{ mb: 0.6, '&:last-child': { mb: 0 } }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.2 }}>
                <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact), fontWeight: 700 }}>
                  {rec.statusLabel}
                </Box>
                {rec.method && (
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>{rec.method}</Box>
                )}
                {rec.counselorName && (
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>{rec.counselorName}</Box>
                )}
                {rec.counselledAt && (
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>
                    {rec.counselledAt}
                  </Box>
                )}
                {rec.checklistProgress && (
                  <Box sx={{ fontSize: hardcopyFontSize('emphasis', compact) }}>
                    Checklist: {rec.checklistProgress}
                  </Box>
                )}
              </Box>
              {rec.declinedReason && (
                <Box sx={{ fontSize: hardcopyFontSize('body', compact) }}>
                  Reason: {rec.declinedReason}
                </Box>
              )}
              {rec.comments && (
                <Box sx={{ fontSize: hardcopyFontSize('body', compact), fontStyle: 'italic' }}>
                  {rec.comments}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <HardcopySignatures
        signatures={signatures}
        rxNumber={rxNumber}
        currentUserInitials={currentUserInitials}
        currentUserEmail={currentUserEmail}
        editable={editable}
        onSign={onSign}
        onDeleteSignature={handleDeleteClick}
        compact={compact}
        patientId={patientId}
        onAddNote={() => setNoteDialogOpen(true)}
        verificationPhotos={verificationPhotos}
        onOpenVerificationPhoto={onOpenVerificationPhoto}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Remove Signature?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove your{' '}
            <strong>{signatureToDelete?.signatureType.replace(/_/g, ' ')}</strong> signature from
            Rx #{rxNumber}?
            <br />
            <br />
            This action cannot be undone. You will need to sign again if required.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove Signature'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Add Note — Rx #{rxNumber}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="Enter verification note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)} disabled={savingNote}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained" disabled={savingNote || !noteText.trim()}>
            {savingNote ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fill History Table (GP-59: Drug Name, status labels, DIN change flagging) */}
      {hardcopyData.history && hardcopyData.history.length > 0 && (
        <Box className="history-section" sx={{ mt: 4, borderTop: '2px solid #000', pt: 2 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: hardcopyFontSize('emphasis', compact),
              marginBottom: 8,
            }}
          >
            Fill History
          </div>
          {/* GP-1163: the history grid stays on the 10.5pt "notes" tier — ten
              numeric columns at the 11pt body size overflow the 700px sheet in a
              proportional font. Still a size up from the old 13px Courier. */}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: hardcopyFontSize('detail', compact),
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #000' }}>
                <th style={{ textAlign: 'left', padding: historyCellPadding }}>Rx#</th>
                <th style={{ textAlign: 'left', padding: historyCellPadding }}>Drug</th>
                <th style={{ textAlign: 'left', padding: historyCellPadding }}>Date</th>
                <th style={{ textAlign: 'left', padding: historyCellPadding }}>Status</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Qty</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Days</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Int</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Total</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Pat Pay</th>
                <th style={{ textAlign: 'right', padding: historyCellPadding }}>Ins Pay</th>
              </tr>
            </thead>
            <tbody>
              {hardcopyData.history.map((item, index) => {
                // GP-59 Requirement 3: Detect DIN changes compared to the previous fill
                const prevItem = index < hardcopyData.history!.length - 1 ? hardcopyData.history![index + 1] : null;
                const dinChanged = prevItem && item.din && prevItem.din && item.din !== prevItem.din;

                return (
                  <tr
                    key={item.rxNumber}
                    // PRX-32: the shaded rows read as "dimmed for no reason" — say what
                    // the shading means on hover.
                    title={
                      dinChanged
                        ? 'DIN changed compared with the previous fill'
                        : item.rxNumber === hardcopyData.rxNumber
                          ? 'This fill (the Rx shown above)'
                          : undefined
                    }
                    style={{
                      cursor: onRxClick ? 'pointer' : 'default',
                      backgroundColor: dinChanged
                        ? '#fff3e0'
                        : item.rxNumber === hardcopyData.rxNumber ? '#f0f0f0' : 'transparent',
                      borderBottom: '1px solid #eee'
                    }}
                    onClick={() => onRxClick && onRxClick(item.rxNumber)}
                  >
                    <td style={{ padding: historyCellPadding }}>
                      {item.rxNumber}
                      {dinChanged && (
                        <span style={{ color: '#e65100', fontWeight: 700, marginLeft: 4 }} title={`DIN changed from ${prevItem.din} to ${item.din}`}>
                          *
                        </span>
                      )}
                    </td>
                    {/* GP-1163: this cell ellipsis-truncates with no way to see the
                        rest, and the larger history font fits ~one word less than
                        the old 13px Courier did. Surface the full drug name on
                        hover so the bigger type doesn't cost information. */}
                    <td
                      style={{ padding: historyCellPadding, maxWidth: compact ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.drugName || undefined}
                    >
                      {item.drugName || '-'}
                    </td>
                    <td style={{ padding: historyCellPadding }}>{formatDate(item.fillDate, 'dd-MMM-yyyy')}</td>
                    <td style={{ padding: historyCellPadding }}>
                      {typeof item.status === 'number' ? getRxStatusLabel(item.status) : describePmsGate(item.pmsGate).label}
                    </td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>{item.dispQty}</td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>{item.daysSupply}</td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>{item.daysInterval !== null ? item.daysInterval : '-'}</td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>${(Number(item.retailPrice) || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>${(Number(item.customerPrice) || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', padding: historyCellPadding }}>${(Number(item.insurancePays) || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* GP-59: DIN change legend */}
          {hardcopyData.history.some((item, index) => {
            const prevItem = index < hardcopyData.history!.length - 1 ? hardcopyData.history![index + 1] : null;
            return prevItem && item.din && prevItem.din && item.din !== prevItem.din;
          }) && (
            <div style={{ fontSize: hardcopyFontSize('detail', compact), color: '#e65100', fontWeight: 600, marginTop: 4 }}>
              * DIN changed from previous fill
            </div>
          )}
        </Box>
      )}
      </div>
    </Paper>
  );
};

export default HardcopyViewer;
